import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { messages } from './messages';
import { RateViewerView } from './views/rate-viewer-view';
import { WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { useTrackRateBrowser } from './use-track-rate-browser';

export const useRateViewerError = ({ filteredRateGroups, isLoadingRates, selectedView }) => {
  const trackRateBrowser = useTrackRateBrowser();
  const [hasDismissedErrorMessage, setHasDismissedErrorMessage] = useState(false);
  const [hasTrackedAllRatesError, setHasTrackedAllRatesError] = useState(false);
  const [hasTrackedExpander, setHasTrackedExpander] = useState(false);

  const intl = useIntl();

  const accountsWithErrors = useMemo(() => {
    return filteredRateGroups.reduce((acc, sellerProviderRate) => {
      if (sellerProviderRate.errorMessage) {
        acc.push(sellerProviderRate.nonAccountName);
      }
      return acc;
    }, []);
  }, [filteredRateGroups]);

  const resetErrorState = useCallback(() => {
    setHasDismissedErrorMessage(false);
    setHasTrackedAllRatesError(false);
    setHasTrackedExpander(false);
  }, []);

  const trackExpander = useCallback(
    (isExpanded) => {
      if (!isExpanded || hasTrackedExpander) {
        return;
      }
      trackRateBrowser(WorkflowSteps.RATE_BROWSER_ALL_RATES_ERROR_BANNER_EXPANDED);
      setHasTrackedExpander(true);
    },
    [hasTrackedExpander, trackRateBrowser],
  );

  const dismissErrorMessage = useCallback(() => {
    setHasDismissedErrorMessage(true);
    trackRateBrowser(WorkflowSteps.RATE_BROWSER_ALL_RATES_ERROR_BANNER_DISMISSED);
  }, [trackRateBrowser]);

  const allRatesErrorMessage = useMemo(() => {
    const accountList = intl.formatList(accountsWithErrors, {
      type: 'conjunction',
    });
    const errorMessage = intl.formatMessage(messages.allRatesErrorMessage, {
      accountsWithErrors: accountList,
    });

    return [errorMessage];
  }, [accountsWithErrors, intl]);

  const shouldShowAllRatesError = useMemo(
    () => selectedView === RateViewerView.AllRates && !isLoadingRates && accountsWithErrors.length > 0 && !hasDismissedErrorMessage,
    [accountsWithErrors.length, hasDismissedErrorMessage, isLoadingRates, selectedView],
  );

  useEffect(() => {
    if (!shouldShowAllRatesError || hasTrackedAllRatesError) {
      return;
    }
    trackRateBrowser(WorkflowSteps.RATE_BROWSER_ALL_RATES_ERROR_BANNER_SHOWN);
    setHasTrackedAllRatesError(true);
  }, [hasTrackedAllRatesError, shouldShowAllRatesError, trackRateBrowser]);

  return useMemo(() => {
    return {
      allRatesErrorMessage,
      shouldShowAllRatesError,
      dismissErrorMessage,
      onToggleErrorMessageExpander: trackExpander,
      resetErrorState,
    };
  }, [allRatesErrorMessage, dismissErrorMessage, resetErrorState, shouldShowAllRatesError, trackExpander]);
};
